import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  lazy,
  Suspense,
} from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Draggable Scroll
import { useDraggable } from 'react-use-draggable-scroll';

// Material
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import Grid from '@material-ui/core/Grid';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Divider from '@material-ui/core/Divider';
import Switch from '@material-ui/core/Switch';

// Material Icons
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';

// Date FNS
import format from 'date-fns/format';

// Lodash
import { groupBy } from 'lodash';

// Helpers
import { currencyFormat } from 'helpers/currencyFormat';
import { difference } from 'helpers/diffTimes';

// Types
import { TripReportInterface } from 'store/ducks/trips/types';
import { ApplicationState } from 'store';

// Actions
import * as SearchActions from 'store/ducks/searchs/actions';

// Constant
import IMAGES from 'constants/images';
import { CONST_LINES_OBJECT } from 'constants/lines';

// Components
import { DialogueExport } from 'components';
import { TableRow as Row } from '../TableRow';

// Styles
import useStyles from './tableTravel.styles';

const TotalCountComponent = lazy(() =>
  import('components/TotalCountComponent'),
);

interface StateProps {
  setTableReference: (arg: any) => void;
  setTableCSV: (arg: any) => void;
  tableReference: any;
  csvRef: any;
  searchTripReportList?: TripReportInterface[];
}

type Props = StateProps;

const TableTravel: React.FC<Props> = ({
  setTableReference,
  setTableCSV,
  tableReference,
  csvRef,
  searchTripReportList,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const parentRef: any = useRef();
  const { events } = useDraggable(parentRef);

  const dialog = useSelector((state: ApplicationState) => state.dialogs.dialog);

  const initialTripReport = useSelector((state: ApplicationState) =>
    searchTripReportList && searchTripReportList.length
      ? searchTripReportList
      : state.trips.tripReport || [],
  );

  const kmType = useSelector((state: ApplicationState) => state.trips.kmType);
  const [benefitsList, setBenefitsList] = useState<string[]>([]);
  const [openCollapseAll, setOpenCollapseAll] = useState(true);
  const [isAgrouped, setIsAgrouped] = useState(false);
  const [tripReportList, setTripReportList] = useState<TripReportInterface[]>(
    [],
  );

  useEffect(() => {
    if (initialTripReport.length >= 0) {
      setTripReportList(initialTripReport);
    }
  }, [initialTripReport]);

  const computeValuesCount = useMemo(
    () => (totalItem: string) => {
      if (Array.isArray(tripReportList) && tripReportList.length > 0) {
        return tripReportList
          .map((item: any) => {
            if (item?.EntriesResume) {
              return item.EntriesResume[totalItem];
            }
            return 0;
          })
          .reduce((total, value) => total! + value!, 0);
      }
      return 0;
    },
    [tripReportList],
  );

  const totalTicket = useMemo(() => computeValuesCount('quantity'), [
    computeValuesCount,
  ]);

  const totalValue = useMemo(() => computeValuesCount('totalValue'), [
    computeValuesCount,
  ]);

  const totalPaid = useMemo(() => computeValuesCount('paidValue'), [
    computeValuesCount,
  ]);
  const totalKm = useMemo(
    () => (km: number, idleKm: number) => (km + idleKm).toFixed(2),
    [],
  );

  const renderBenefitValue = useMemo(
    () => (trip: TripReportInterface, benefit: string) => {
      if (trip.EntriesResume?.entries?.benefits) {
        const { benefits } = trip.EntriesResume.entries;
        return benefits.hasOwnProperty(benefit)
          ? // @ts-ignore
            Number(benefits[benefit].quantity)
            ? // @ts-ignore
              benefits[benefit].quantity
            : 0
          : // @ts-ignore
            (benefits[benefit] = 0);
      }
      return 0;
    },
    [],
  );

  useEffect(
    () =>
      // unmounting
      () => {
        dispatch(
          SearchActions.setSelectedItems({
            selectedItems: [],
          }),
        );
      },
    [dispatch],
  );

  // generating the table csv
  useEffect(() => {
    let csv = '';
    if (tripReportList) {
      let header = '';
      let contentAux2 = '';

      Object.entries(
        groupBy(tripReportList, (trip) => trip.TransportEmployee?.name),
      ).flatMap(([key, tripReport]) =>
        tripReport
          .sort((a: any, b: any) => {
            if (a?.LineSection && b?.LineSection) {
              return a?.LineSection?.Line?.name.localeCompare(
                b?.LineSection?.Line?.name,
              );
            }
          })
          .map((trip) => {
            const benefits = trip.EntriesResume?.entries?.benefits;
            if (benefits) {
              for (const benefit of Object.keys(benefits)) {
                if (!benefitsList.includes(benefit)) {
                  benefitsList.push(benefit);
                }
              }
            }
          }),
      );

      if (Array.isArray(benefitsList)) {
        benefitsList.forEach((benefit) => (contentAux2 += `${benefit};`));
      }

      const kmIdleHeader = kmType === 'discriminated' ? 'Km. Ocioso;' : '';

      header = `Motorista;Linha;Sentido;Viagem;Carro;Data;Inicio;Fim;Tempo;Km;${kmIdleHeader}R$/Km;IPK;IPKE;Passagens Processadas;Total de Embarques;Valor Transportado;${contentAux2}Total\n`;
      csv += header;

      csv += Object.entries(
        groupBy(tripReportList, (trip) => trip.TransportEmployee?.name),
      )
        .flatMap(([key, tripReport]) =>
          tripReport
            .sort((a: any, b: any) => {
              if (a?.LineSection && b?.LineSection) {
                return a?.LineSection?.Line?.name.localeCompare(
                  b?.LineSection?.Line?.name,
                );
              }
            })
            .map((trip: any) => {
              const benefits = trip.EntriesResume?.entries?.benefits;
              if (benefits) {
                for (const benefit of Object.keys(benefits)) {
                  if (!benefitsList.includes(benefit)) {
                    benefitsList.push(benefit);
                  }
                }
              }

              let contentAux = '';
              benefitsList.forEach(
                (benefit) =>
                  (contentAux += `${renderBenefitValue(trip, benefit)};`),
              );

              const kmIdleValue =
                kmType === 'discriminated' ? `${trip.idleKm?.toFixed(2)};` : '';

              const totalSeparator =
                Array.isArray(benefitsList) && benefitsList.length > 0
                  ? ''
                  : ';';

              const resume = trip?.resume ? JSON.parse(trip?.resume) : null;

              return `${trip?.TransportEmployee?.name};${
                trip.LineSection?.Line
                  ? `${trip.LineSection?.Line?.prefix || ''} - ${
                      trip.LineSection?.Line?.name
                    }`
                  : '--'
              };${
                trip.LineSection?.way === CONST_LINES_OBJECT.WAYS.GO.KEY
                  ? CONST_LINES_OBJECT.WAYS.GO.VALUE
                  : CONST_LINES_OBJECT.WAYS.BACK.VALUE
              };${(trip?.id).slice(0, 8) || '--'};${trip?.Car?.prefix ||
                '--'};${
                trip.startedAt
                  ? format(new Date(trip.startedAt), 'dd/MM/yyyy')
                  : '--'
              };${
                trip.startedAt
                  ? format(new Date(trip.startedAt), 'HH:mm:ss')
                  : '--'
              };${
                trip?.closedAt
                  ? format(new Date(trip.closedAt), 'HH:mm:ss')
                  : '--'
              };${
                trip?.closedAt && trip.startedAt
                  ? difference(trip.closedAt, trip.startedAt)
                  : '--'
              };${
                kmType === 'total'
                  ? totalKm(trip.km, trip.idleKm)
                  : trip.km?.toFixed(2)
              };${kmIdleValue}${currencyFormat({
                value: trip.valuePerKm || 0,
              })};${trip.ipk?.toFixed(2) || '--'};${trip.ipke?.toFixed(2) ||
                '--'};${trip?.EntriesResume?.quantity ||
                '--'};${trip?.totalPassengers || '--'};${
                resume
                  ? currencyFormat({ value: resume?.totalValue || 0 })
                  : '--'
              };${contentAux}${totalSeparator}\n`;
            }),
        )
        .join('');

      setTableCSV(csv);
    }
  }, [
    tripReportList,
    setTableCSV,
    benefitsList,
    kmType,
    totalKm,
    renderBenefitValue,
  ]);

  return (
    <Grid ref={(node: unknown) => setTableReference(node)}>
      <Grid
        container
        justifyContent='space-between'
        className={classes.gridContainer}>
        <Grid container justifyContent='flex-end' alignItems='center'>
          {dialog.open && dialog.isDialogPrint && (
            <Typography color='inherit' variant='h4'>
              Relatórios de Operação • Meia Viagem
            </Typography>
          )}
          <Box display='flex' alignItems='center'>
            <Box className={classes.resume}>
              <Box className={classes.contentImage}>
                <img
                  src={IMAGES.REPORTS_ICONS.CREDIT_PORTFOLIO}
                  alt='credit portfolio'
                />
              </Box>
              <Divider
                className={classes.dividerVertical}
                orientation='vertical'
                flexItem
              />{' '}
              <Grid className={classes.contentTotalEntry}>
                <Typography className={classes.subTiltle}>
                  Passagens Vendidas
                </Typography>
                <Typography className={classes.value}>{totalTicket}</Typography>
              </Grid>
            </Box>
            <Box className={classes.resume}>
              <Box className={classes.contentImage}>
                <img src={IMAGES.REPORTS_ICONS.TOTAL_ENTRY} alt='total entry' />
              </Box>
              <Divider
                className={classes.dividerVertical}
                orientation='vertical'
                flexItem
              />{' '}
              <Grid className={classes.contentTotalEntry}>
                <Typography className={classes.subTiltle}>
                  Transacionado
                </Typography>
                <Typography className={classes.value}>
                  {currencyFormat({ value: totalValue! })}
                </Typography>
              </Grid>
              <Divider
                className={classes.dividerVertical}
                orientation='vertical'
                flexItem
              />{' '}
              <Grid className={classes.contentTotalEntry}>
                <Typography className={classes.subTiltle}>Pago</Typography>
                <Typography className={classes.value}>
                  {currencyFormat({ value: totalPaid! })}
                </Typography>
              </Grid>
              <Divider
                className={classes.dividerVertical}
                orientation='vertical'
                flexItem
              />{' '}
              <Grid className={classes.contentTotalEntry}>
                <Typography className={classes.subTiltle}>Aberto</Typography>
                <Typography className={classes.value}>
                  {currencyFormat({ value: totalValue! - totalPaid! })}
                </Typography>
              </Grid>
            </Box>
            <Box>
              <DialogueExport
                isDialogPrint
                tableInstance={tableReference}
                csvRef={csvRef}
                pdfName='tabela diario de meia viagem'
                pdf
                csv
              />
            </Box>
          </Box>
          <Box
            display='flex'
            width='100%'
            alignItems='center'
            justifyContent='flex-end'
            mt={1}>
            <Box display='flex'>
              <Typography style={{ marginRight: 8 }} align='center'>
                Agrupar Viagens
              </Typography>

              <Switch
                checked={isAgrouped}
                onChange={() => setIsAgrouped((s) => !s)}
                className={classes.switch}
              />
            </Box>

            {Object.entries(
              groupBy(tripReportList, (trip) => trip.TransportEmployee?.name),
            )?.length > 0 && (
              <Box width='100%' display='flex' justifyContent='flex-end' ml={2}>
                <Suspense fallback='Carregando...'>
                  <TotalCountComponent
                    count={
                      Object.entries(
                        groupBy(
                          tripReportList,
                          (trip) => trip.TransportEmployee?.name,
                        ),
                      )?.length || 0
                    }
                    title='usuários'
                    position='right'
                  />
                </Suspense>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      <Box m={2} className={classes.contentTable}>
        <TableContainer className={classes.table} {...events} ref={parentRef}>
          <Table aria-label='collapsible table'>
            <TableHead>
              <TableRow>
                <TableCell variant='head' align='left'>
                  <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='space-between'>
                    <Box display='flex' alignItems='center'>
                      <IconButton
                        aria-label='expand row'
                        size='small'
                        onClick={() => setOpenCollapseAll((o) => !o)}>
                        {openCollapseAll ? (
                          <KeyboardArrowUpIcon style={{ color: '#fff' }} />
                        ) : (
                          <KeyboardArrowDownIcon style={{ color: '#fff' }} />
                        )}
                      </IconButton>
                      Motorista
                    </Box>
                  </Box>
                </TableCell>{' '}
                <TableCell variant='head' align='left'>
                  Linha
                </TableCell>
                <TableCell variant='head' align='left'>
                  Sentido
                </TableCell>
                <TableCell variant='head' align='left'>
                  Viagem
                </TableCell>
                <TableCell variant='head' align='left'>
                  Carro
                </TableCell>
                <TableCell variant='head' align='left'>
                  Data
                </TableCell>
                <TableCell variant='head' align='left'>
                  Inicio
                </TableCell>
                <TableCell variant='head' align='left'>
                  Fim
                </TableCell>
                <TableCell variant='head' align='left'>
                  Tempo
                </TableCell>
                <TableCell variant='head' align='left'>
                  Km
                </TableCell>
                {kmType === 'discriminated' && (
                  <TableCell variant='head' align='left'>
                    Km. Ocioso
                  </TableCell>
                )}
                <TableCell variant='head' align='left'>
                  R$/Km
                </TableCell>
                <TableCell variant='head' align='left'>
                  IPK
                </TableCell>
                <TableCell variant='head' align='left'>
                  IPKE
                </TableCell>
                <TableCell variant='head' align='left'>
                  Passagens Processadas
                </TableCell>
                <TableCell variant='head' align='left'>
                  Total de Embarques
                </TableCell>
                <TableCell variant='head' align='left'>
                  Valor Transportado
                </TableCell>
                {Array.isArray(benefitsList) &&
                  benefitsList.map((benefit) => (
                    <TableCell key={benefit} variant='head' align='left'>
                      {benefit}
                    </TableCell>
                  ))}
                <TableCell>Total</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            {tripReportList.length > 0 &&
              Object.entries(
                groupBy(
                  tripReportList,
                  (trip) =>
                    `${trip.TransportEmployee?.registry} - ${trip.TransportEmployee?.name}`,
                ),
              ).map(([key, tripReport], index) => (
                <Row
                  key={`row-${key}`}
                  driverName={key}
                  tripReport={tripReport as TripReportInterface[]}
                  index={index}
                  openCollapseAll={openCollapseAll}
                  isAgrouped={isAgrouped}
                />
              ))}
          </Table>
        </TableContainer>
      </Box>
    </Grid>
  );
};

export { TableTravel };
export default TableTravel;
